import React, { useEffect, useState } from 'react';
import RichTextEditor from "./SimpleEditor"; // Import the RichTextEditor component
import 'draft-js/dist/Draft.css';
import { API_ENDPOINTS } from "../config/apiConfig";

const NOTE_URL = API_ENDPOINTS.note;

const DEFAULT_NOTE_RAW = '{"blocks":[{"key":"title","text":"","type":"header-one","depth":0,"inlineStyleRanges":[],"entityRanges":[],"data":{}}],"entityMap":{}}';

const NewNote = ({selectedNote, onNewNote, onNotesChanged, setSelectedNote, editorRef, readOnly, onRefineStateChange, highlightQuery, framePaddingOffset, editorLabelAction, refinedEntities, onConceptClick, onRefinedStaticViewChange, onSeeEntitiesRelations}) => {
  const [noteId, setNoteId] = useState(null);
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");

  // Function to handle saving the note
  const handleSaveNote = async (rawContent, saveMode = "draft") => {
    const token = localStorage.getItem("authToken"); // Assuming the token is stored in localStorage
    const noteData = {
      id: noteId,
      title,
      content: rawContent,
      date: new Date().toISOString().split("T")[0], // Current date in YYYY-MM-DD format
      save_mode: saveMode,
    };

    try {
      const response = await fetch(NOTE_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`,
        },
        body: JSON.stringify(noteData),
      });

      if (response.ok) {
        const result = await response.json();
        if (onNotesChanged) onNotesChanged(); // Notify parent to refresh notes
        setNoteId(result.id); // Always set the note ID after saving
        const isRefined = result.is_refined === true
          || result.refined === true
          || result.refine_state === 'refined';
        // Update parent selectedNote with real note object
        if (setSelectedNote) {
          setSelectedNote({
            id: result.id,
            title,
            content: rawContent,
            content_hash: result.content_hash,
            is_refined: isRefined,
            refined: isRefined,
            refine_state: result.refine_state || (isRefined ? 'refined' : 'draft'),
            refine_status: result.refine_status || 'idle',
            refine_error: result.refine_error || '',
            refine_requested_at: result.refine_requested_at || null,
            refine_started_at: result.refine_started_at || null,
            refine_completed_at: result.refine_completed_at || null,
            queued_content_hash: result.queued_content_hash || null,
            completed_content_hash: result.completed_content_hash || null,
          });
        }
        return {
          success: true,
          noteId: result.id,
          content_hash: result.content_hash,
          is_refined: isRefined,
          refined: isRefined,
          refine_state: result.refine_state || (isRefined ? 'refined' : 'draft'),
          refine_status: result.refine_status || 'idle',
          refine_error: result.refine_error || '',
        };
      } else {
        return { success: false };
      }
    } catch (error) {
      return { success: false };
    }
  };

  console.log("NewNote component rendered");
  console.log("Selected Note:", selectedNote);

  useEffect(() => {
    console.log("selectedNote changed:", selectedNote);
    // Accept both null and 'new' objects as triggers for a blank editor
    if (!selectedNote || selectedNote.id === 'new' || selectedNote._forceOpen) {
      console.log("Clearing content for new note");
      setContent(DEFAULT_NOTE_RAW);
      setTitle('');
      setNoteId(null);
    } else {
      console.log("Populating selected note:", selectedNote);
      setContent(selectedNote.content);
      setTitle(selectedNote.title);
      setNoteId(selectedNote.id);
    }
  }, [selectedNote]);
  return (
    <div style={styles.container}>
      <div style={styles.main}>
        <section style={styles.editorSection}>
          <RichTextEditor
            key={selectedNote?.id || 'new-note'}
            ref={editorRef}
            onSave={handleSaveNote}
            initialContent={content}
            onTitleChange={setTitle}
            readOnly={readOnly}
            onRefineStateChange={onRefineStateChange}
            highlightQuery={highlightQuery}
            framePaddingOffset={framePaddingOffset}
            noteMeta={selectedNote}
            editorLabelAction={editorLabelAction}
            refinedEntities={refinedEntities}
            onConceptClick={onConceptClick}
            onRefinedStaticViewChange={onRefinedStaticViewChange}
            onSeeEntitiesRelations={onSeeEntitiesRelations}
          />
        </section>
      </div>
    </div>
  );
};

const styles = {
  container: {
    fontFamily: "Roboto, sans-serif",
    flex: 1,
    display: "flex",
    flexDirection: "column",
    minHeight: 0, // Allow shrinking in flex parent
    backgroundColor: "transparent", // Remove background, let parent handle
    padding: 0, // Remove padding, let parent handle
  },
  main: {
    display: "flex",
    flex: 1,
    minHeight: 0, // Allow shrinking in flex parent
  },
  editorSection: {
    flex: 1,
    minHeight: 0, // Allow shrinking in flex parent
    padding: 0, // Remove padding, let parent handle
    backgroundColor: "transparent", // Remove background, let parent handle
    display: "flex",
    flexDirection: "column",
    gap: "10px",
  },
  header: {
    textAlign: "center",
    padding: "20px",
    backgroundColor: "#fff",
  },
  sidebar: {
    width: "30%",
    backgroundColor: "#fff",
    padding: "20px",
    overflowY: "auto",
    // Removed height: "70vh"
  },
  notePreview: {
    marginBottom: "20px",
    backgroundColor: "#fff",
    padding: "15px",
    borderRadius: "8px",
  },
  divider: {
    border: "none",
    borderTop: "1px solid #ccc",
    margin: "10px 0",
  },
  editorBar: {
    display: "flex",
    gap: "5px",
    marginBottom: "10px",
  },
  editorButton: {
    padding: "5px 10px",
    backgroundColor: "#fff",
    border: "1px solid #ccc",
    borderRadius: "4px",
    cursor: "pointer",
  },
  titleInput: {
    padding: "10px",
    fontSize: "18px",
    border: "1px solid #ccc",
    borderRadius: "4px",
    marginBottom: "10px",
  },
  textarea: {
    flex: 1,
    padding: "10px",
    fontSize: "16px",
    border: "1px solid #ccc",
    borderRadius: "4px",
    resize: "none",
    minHeight: "300px",
  },
  saveButton: {
    alignSelf: "flex-start",
    padding: "10px 20px",
    fontSize: "16px",
    backgroundColor: "#492099",
    color: "#fff",
    border: "none",
    borderRadius: "4px",
    cursor: "pointer",
  },
};

export default NewNote;
